/**
 * Chart tokens.
 *
 * The whole interface renders on a dark cleanroom surface, so these are the
 * dark-mode steps, validated as a set against the actual panel surface below
 * rather than against a generic default. Colour is assigned by the job it does:
 *
 *   sequential (magnitude)  remaining film thickness, radical density
 *   diverging  (polarity)   deviation from the wafer mean - fast edge vs fast centre
 *   categorical (identity)  at most three series, in fixed slot order
 *   status     (state)      tool state and out-of-control conditions, never a series
 */

export const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", sans-serif'

/**
 * THE app-wide tokens - the canonical values behind the `:root` custom
 * properties in styles.css, the Ant Design theme (app/theme/antdTheme.ts),
 * and the canvas painters below. One source, three sinks: change a value
 * here and applyThemeTokens() rewrites the CSS vars at boot, the antd theme
 * reads it at module load, and every canvas import follows automatically.
 * The literals in styles.css survive only as a no-JS fallback.
 */
export const APP_TOKENS = {
  surface: '#131a21',
  surfaceRaised: '#1a232c',
  surfaceSunken: '#0d1319',
  ink: '#ffffff',
  ink2: '#c3c2b7',
  inkMuted: '#898781',
  hair: 'rgba(255, 255, 255, 0.1)',
  hairStrong: 'rgba(255, 255, 255, 0.18)',
  accent: '#3987e5',
  good: '#0ca30c',
  goodSoft: 'rgba(12, 163, 12, 0.16)',
  warning: '#fab219',
  critical: '#d03b3b',
  font: FONT_STACK,
} as const

/** Write the tokens onto :root as the CSS custom properties. Once, at boot. */
export function applyThemeTokens(): void {
  if (typeof document === 'undefined') return
  const s = document.documentElement.style
  s.setProperty('--surface', APP_TOKENS.surface)
  s.setProperty('--surface-raised', APP_TOKENS.surfaceRaised)
  s.setProperty('--surface-sunken', APP_TOKENS.surfaceSunken)
  s.setProperty('--ink', APP_TOKENS.ink)
  s.setProperty('--ink-2', APP_TOKENS.ink2)
  s.setProperty('--ink-muted', APP_TOKENS.inkMuted)
  s.setProperty('--hair', APP_TOKENS.hair)
  s.setProperty('--hair-strong', APP_TOKENS.hairStrong)
  s.setProperty('--accent', APP_TOKENS.accent)
  s.setProperty('--good', APP_TOKENS.good)
  s.setProperty('--good-soft', APP_TOKENS.goodSoft)
  s.setProperty('--warning', APP_TOKENS.warning)
  s.setProperty('--critical', APP_TOKENS.critical)
  s.setProperty('--font', APP_TOKENS.font)
}

/** Panel surface every chart draws on. */
export const SURFACE = APP_TOKENS.surface

export const INK = {
  primary: '#ffffff',
  secondary: '#c3c2b7',
  muted: '#898781',
  grid: '#2c2c2a',
  axis: '#383835',
  border: 'rgba(255,255,255,0.10)',
} as const

/**
 * Categorical slots in fixed order. Never cycled, never reassigned by rank -
 * a series keeps its colour when other series are filtered away.
 */
export const SERIES = ['#3987e5', '#d95926', '#199e70'] as const

/** Reserved for tool state. Always shipped with an icon or a label. */
export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const

/** Sequential blue ramp, light to dark, for continuous magnitude. */
const SEQUENTIAL_BLUE = [
  '#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef', '#6da7ec', '#5598e7',
  '#3987e5', '#2a78d6', '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b',
]

/** Diverging blue <-> red about a neutral gray. Poles read as opposite. */
const DIVERGING_COOL = ['#0d366b', '#184f95', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4']
const DIVERGING_WARM = ['#f0b3b3', '#e88b8b', '#dc6060', '#d03b3b', '#a82e2e', '#7d2222']
const DIVERGING_MID = '#383835'

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16)
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(a)
  const [r2, g2, b2] = hexToRgb(b)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const bl = Math.round(b1 + (b2 - b1) * t)
  return `rgb(${r},${g},${bl})`
}

/** Sample the sequential ramp. t = 0 is near-zero (lightest), t = 1 is full. */
export function sequential(t: number): string {
  const x = Math.max(0, Math.min(1, t)) * (SEQUENTIAL_BLUE.length - 1)
  const i = Math.floor(x)
  const f = x - i
  if (i >= SEQUENTIAL_BLUE.length - 1) return SEQUENTIAL_BLUE[SEQUENTIAL_BLUE.length - 1]
  return mix(SEQUENTIAL_BLUE[i], SEQUENTIAL_BLUE[i + 1], f)
}

/**
 * Sample the diverging ramp. t in [-1, 1]; 0 returns the neutral midpoint, so
 * "no deviation" reads as nothing rather than as a colour.
 */
export function diverging(t: number): string {
  const v = Math.max(-1, Math.min(1, t))
  if (Math.abs(v) < 0.02) return DIVERGING_MID
  const arm = v < 0 ? DIVERGING_COOL : DIVERGING_WARM
  const x = Math.abs(v) * (arm.length - 1)
  const i = Math.floor(x)
  const f = x - i
  const lo = v < 0 ? arm[arm.length - 1 - i] : arm[i]
  const hi = v < 0 ? arm[Math.max(arm.length - 2 - i, 0)] : arm[Math.min(i + 1, arm.length - 1)]
  return mix(lo, hi, f)
}

